import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SurveyType, UserProfile } from '@cpd-app/core-module/models';
import {
  ProfileWorkTypeEnum,
  UserPreGoalInput,
  UserProgramGoalInput,
  UserSchoolGoalInput
} from '@cpd-app/generated';
import { AppState } from '@cpd-app/state';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { AuthFacade } from '../auth';
import { CognitoAuthFacade } from '../cognito-auth';

import * as UserProfileActions from './user-profile.actions';
import * as UserProfileSelectors from './user-profile.selectors';
import { CIVILIAN_CODE, MILITARY_TYPE } from '@cpd-app/shared-module/constants';

@Injectable({
  providedIn: 'root'
})
export class UserProfileFacade {
  readonly userProfile$ = this.authFacade.shareToken$.pipe(
    switchMap((shareToken) =>
      shareToken
        ? this.userShareProfile$
        : this.store.select(UserProfileSelectors.selectUserProfile)
    )
  );
  readonly userShareProfile$ = this.store.select(
    UserProfileSelectors.selectUserShareProfile
  );

  readonly isSkillsSurveyComplete$ = this.userProfile$.pipe(
    map((userProfile) => userProfile?.isSkillsSurveyComplete)
  );
  readonly isAnySurveyComplete$ = this.store.select(
    UserProfileSelectors.selectIsAnySurveyComplete
  );
  readonly isAllSurveyComplete$ = this.store.select(
    UserProfileSelectors.selectIsAllSurveyComplete
  );
  readonly militaryBranchCode$ = this.store.select(
    UserProfileSelectors.selectMilitaryBranchCode
  );
  readonly isJstUploaded$ = this.store.select(
    UserProfileSelectors.selectIsJstUploaded
  );
  readonly hasPreGoal$ = this.store.select(
    UserProfileSelectors.selectHasPreGoal
  );

  readonly userPreGoalOccupation$ = this.store.select(
    UserProfileSelectors.selectUserPreGoal
  );
  readonly currentUserLocation$ = this.store.select(
    UserProfileSelectors.selectCurrentUserLocation
  );

  readonly highestUserEducation$ = this.store.select(
    UserProfileSelectors.selectHighestUserEducation
  );

  readonly hideSimilarMilitaryOccupation = this.store
    .select(UserProfileSelectors.selectUserProfile)
    .pipe(
      map(
        (profile) =>
          profile?.militaryBranch?.branchCode === CIVILIAN_CODE ||
          profile?.serveTypeId !== 1
      )
    );
  readonly isCivilian$ = this.store
    .select(UserProfileSelectors.selectUserProfile)
    .pipe(
      map(
        (profile) =>
          profile?.militaryBranch?.branchCode === CIVILIAN_CODE ||
          !profile?.militaryBranch
      )
    );

  readonly isMilitaryActive$ = this.store
    .select(UserProfileSelectors.selectUserProfile)
    .pipe(map((profile) => profile?.serveTypeId === MILITARY_TYPE.Active));

  /**
   * CPD-667. Whether the member is serving on active duty, or null while the
   * profile has not arrived.
   *
   * Distinct from `isMilitaryActive$` above, which answers the same question
   * as a plain boolean and so cannot tell "not active duty" apart from "we do
   * not know yet". Callers that gate a QUESTION on this -- rather than a
   * layout tweak -- need that difference: a boolean seeded false asks nobody
   * for one frame, and a boolean seeded true asks everybody.
   *
   * `api.militaryservetype` has ten rows, of which exactly one (id 1) is
   * Active Duty. The other nine are Veteran and the seven Guard/Reserve
   * components -- all of them military, none of them currently serving in the
   * sense the leaving-or-staying question assumes.
   */
  readonly isActiveDuty$ = this.store
    .select(UserProfileSelectors.selectUserProfile)
    .pipe(
      map((profile) =>
        profile ? profile.serveTypeId === MILITARY_TYPE.Active : null
      )
    );
  readonly saveUserPreGoalsSuccessType =
    UserProfileActions.saveUserPreGoalsSuccess.type;
  readonly userSkillsSurveySavedSuccessType =
    UserProfileActions.userSkillsSurveySavedSuccess.type;
  readonly userInterestsSurveySavedSuccessType =
    UserProfileActions.userInterestsSurveySavedSuccess.type;
  readonly userWorkValuesSurveySavedSuccessType =
    UserProfileActions.userWorkValuesSurveySavedSuccess.type;
  readonly userLifestyleSurveySavedSuccessType =
    UserProfileActions.userLifestyleSurveySavedSuccess.type;
  readonly acceptPrivacyPolicySuccessType =
    UserProfileActions.acceptPrivacyPolicySuccess.type;
  readonly acceptBannerPrivacyPolicySuccessType =
    UserProfileActions.acceptBannerPrivacyPolicySuccess.type;
  readonly updateProfileWorkTypeSuccessType =
    UserProfileActions.updateProfileWorkTypeSuccess.type;

  readonly userProfileLoadIfEmpty$ = this.userProfile$.pipe(
    withLatestFrom(
      this.authFacade.authToken$,
      this.cognitoAuthFacade.isInProgress$
    ),
    map(([userProfile, token, isInProgress]) => {
      const returnToken =
        this.activatedRoute.snapshot.queryParams['returnToken'];

      if (!userProfile && !returnToken && !isInProgress && !!token) {
        this.loadUserProfile();
      }

      return userProfile;
    })
  );

  readonly privacyPolicyAccepted$ = this.store.pipe(
    filter((store) => {
      return !!store.userProfile?.userProfile?.userId;
    }),
    select(UserProfileSelectors.selectPrivacyPolicyAccepted)
  );

  readonly isAirForce$ = this.store.pipe(
    select(UserProfileSelectors.selectIsAirForce)
  );

  readonly isArmy$ = this.store.pipe(select(UserProfileSelectors.selectIsArmy));
  readonly profileWorkType$ = this.store.pipe(
    filter((store) => {
      return !!store.userProfile?.userProfile?.userId;
    }),
    select(UserProfileSelectors.selectProfileWorkType)
  );
  readonly profileWorkTypeSet$ = this.store.pipe(
    filter((store) => {
      return !!store.userProfile?.userProfile?.userId;
    }),
    select(UserProfileSelectors.selectProfileWorkTypeSet)
  );

  /**
   * CPD-654. Emits once the profile in the store belongs to an identified
   * member. Use this to wait for a profile; use `profileWorkTypeSet$` only
   * when the work type itself is what matters.
   */
  readonly userIdSet$ = this.store.pipe(
    filter((store) => {
      return !!store.userProfile?.userProfile?.userId;
    }),
    select(UserProfileSelectors.selectUserIdSet)
  );

  readonly isSpaceForce$ = this.store.pipe(
    select(UserProfileSelectors.selectIsSpaceForce)
  );

  /**
   * Fires when a skills survey save was refused. The survey watches this so a
   * failed write is visible to the member rather than only to the toast, which
   * suppresses anything that looks like an authorization problem.
   */
  public readonly skillsSurveySaveFailed$ = this.actions$.pipe(
    ofType(UserProfileActions.userSkillsSurveySavedFailure)
  );

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private authFacade: AuthFacade,
    private activatedRoute: ActivatedRoute,
    private cognitoAuthFacade: CognitoAuthFacade
  ) {}

  loadUserProfile() {
    this.store.dispatch(UserProfileActions.loadUserProfile());
  }

  loadShareUserProfile() {
    this.store.dispatch(UserProfileActions.loadShareUserProfile());
  }

  deleteUserProfile() {
    this.store.dispatch(UserProfileActions.deleteUserProfile());
  }

  invalidateUserProfile() {
    this.store.dispatch(UserProfileActions.invalidateUserProfile());
  }

  saveUserSkillsSurvey(userProfile: UserProfile) {
    this.store.dispatch(
      UserProfileActions.saveUserSkillsSurvey({ userProfile })
    );
  }

  saveUserLifestyleSurvey(userProfile: UserProfile) {
    this.store.dispatch(
      UserProfileActions.saveUserLifestyleSurvey({ userProfile })
    );
  }

  saveUserInterestsSurvey(userProfile: UserProfile) {
    this.store.dispatch(
      UserProfileActions.saveUserInterestsSurvey({ userProfile })
    );
  }

  saveUserWorkValuesSurvey(userProfile: UserProfile) {
    this.store.dispatch(
      UserProfileActions.saveUserWorkValuesSurvey({ userProfile })
    );
  }

  updateUserProfileFromMilitarySkills(userProfile: UserProfile) {
    this.store.dispatch(
      UserProfileActions.updateUserProfileFromMilitarySkills({ userProfile })
    );
  }

  updateUserProfileFromOtherSkills(userProfile: UserProfile) {
    this.store.dispatch(
      UserProfileActions.updateUserProfileFromOtherSkills({ userProfile })
    );
  }

  /**
   * ASVAB.9. After any successful `saveAsvabScoreSet`, from the profile the
   * mutation answered with, so the three places that collect scores agree.
   */
  asvabScoresSaved(
    profile: Pick<UserProfile, 'asvabScores'> & {
      asvabScoreSet?: { scale: string; score: number }[] | null;
    } | null | undefined
  ) {
    if (!profile) {return;}
    this.store.dispatch(
      UserProfileActions.asvabScoresSaved({
        asvabScoreSet: profile.asvabScoreSet ?? [],
        asvabScores: profile.asvabScores ?? null
      })
    );
  }

  updateUserProfileWorkTransition(workTransition: number) {
    this.store.dispatch(
      UserProfileActions.updateUserProfileWorkTransition({
        monthlyTransition: workTransition
      })
    );
  }

  /** Months until separation; null saves "no date" (walkthrough 09-29 #19). */
  updateUserProfileMilitaryTransition(militaryTransition: number | null) {
    this.store.dispatch(
      UserProfileActions.updateUserProfileMilitaryTransition({
        monthlyTransition: militaryTransition
      })
    );
  }

  public saveUserProgramPreGoals(
    values: UserProgramGoalInput,
    surveyType: SurveyType
  ): void {
    this.store.dispatch(
      UserProfileActions.saveUserProgramGoals({ values, surveyType })
    );
  }
  public saveUserSchoolPreGoals(
    values: UserSchoolGoalInput,
    surveyType: SurveyType
  ): void {
    this.store.dispatch(
      UserProfileActions.saveUserSchoolGoals({ values, surveyType })
    );
  }

  public saveUserPreGoals(
    values: UserPreGoalInput,
    surveyType: SurveyType
  ): void {
    this.store.dispatch(
      UserProfileActions.saveUserPreGoals({ values, surveyType })
    );
  }

  public initUserMatches(): void {
    this.store.dispatch(UserProfileActions.initUserMatches());
  }

  public acceptPrivacyPolicy(): void {
    this.store.dispatch(UserProfileActions.acceptPrivacyPolicy());
  }

  public acceptBannerPrivacyPolicy(): void {
    this.store.dispatch(UserProfileActions.acceptBannerPrivacyPolicy());
  }

  public updateProfileWorkType(workType: ProfileWorkTypeEnum): void {
    this.store.dispatch(UserProfileActions.updateProfileWorkType({ workType }));
  }

  public saveMilitaryServeType(serveTypeId: number): void {
    this.store.dispatch(
      UserProfileActions.saveMilitaryServeType({ serveTypeId })
    );
  }

  public saveMilitaryAirbaseLocation(airbaseLocationId: string): void {
    this.store.dispatch(
      UserProfileActions.saveMilitaryAirbaseLocation({ airbaseLocationId })
    );
  }
  public saveMilitarySpacebaseLocation(spacebaseLocationId: string): void {
    this.store.dispatch(
      UserProfileActions.saveMilitarySpacebaseLocation({ spacebaseLocationId })
    );
  }

  public saveMilitaryInstallation(installationId: string): void {
    this.store.dispatch(
      UserProfileActions.saveMilitaryInstallation({ installationId })
    );
  }

  public notifyUserCompletesAnySurvey() {
    this.store.dispatch(UserProfileActions.userCompletesAnySurvey());
  }
}
